<script setup>
import '@/assets/css/common.css';
import DefaultPanel from '@/components/DefaultPanel.vue';
import PreviewPanel from '@/components/SingleFileViewComponent.vue';
import { useLayout } from '@/layout/composables/layout';
import { useSidebarStore } from '@/store/sidebar-store';
import { useTipStore } from '@/store/tip-store';
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, watch } from 'vue';
import AppFooter from './AppFooter.vue';
import AppTopbar from './AppTopbar.vue';
let baseUrl = import.meta.env.VITE_APP_API_URL;
const isDevelopment = import.meta.env.VITE_APP_ENV === 'development';
const BannerComponent = defineAsyncComponent({
    loader: () => import('@/components/BannerComponent.vue'),
    loadingComponent: null,
    delay: 0
});

const isPopupOpen = ref(false);
const { layoutConfig, layoutState, resetMenu } = useLayout();
const tipStore = useTipStore();
const sidebarStore = useSidebarStore();
const outsideClickListener = ref(null);
const previewFile = ref({
    url: '',
    type: ''
});

watch(
    () => sidebarStore.isSidebarActive,
    (newVal) => {
        if (newVal) {
            bindOutsideClickListener();
        } else {
            unbindOutsideClickListener();
        }
    }
);

const containerClass = computed(() => {
    return {
        'layout-overlay': layoutConfig.menuMode === 'overlay',
        'layout-static': layoutConfig.menuMode === 'static',
        'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
        'layout-overlay-active': layoutState.overlayMenuActive,
        'layout-mobile-active': layoutState.staticMenuMobileActive
    };
});

const bindOutsideClickListener = () => {
    if (!outsideClickListener.value) {
        outsideClickListener.value = (event) => {
            if (isOutsideClicked(event)) {
                sidebarStore.setSidebarState(false);
                resetMenu();
            }
        };
        //document.addEventListener('click', outsideClickListener.value);
    }
};

const unbindOutsideClickListener = () => {
    if (outsideClickListener.value) {
        // document.removeEventListener('click', outsideClickListener.value);
        outsideClickListener.value = null;
    }
};

const isOutsideClicked = (event) => {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarEl = document.querySelector('.layout-menu-button');

    return !(sidebarEl.isSameNode(event.target) || sidebarEl.contains(event.target) || topbarEl.isSameNode(event.target) || topbarEl.contains(event.target));
};

const closePreview = () => {
    previewFile.value = { url: '', type: '' };
};

const onKeydown = async (event) => {
    if (!isDevelopment) return;
    let url = '';
    let windowFeatures = '';
    switch (event.key) {
        case 'F1':
            url = `${baseUrl}/docs/view`;
            windowFeatures = 'width=1200,height=900,resizable=yes,scrollbars=yes,status=yes';
            // if (tipStore.showPopup) {
            //     tipStore.closePopup();
            // } else {
            //     tipStore.openPopup('팝업', '내용');
            // }
            break;
        case 'F2':
            url = 'https://docs.google.com/spreadsheets/d/1YDgWE4inc9Et157NcvaXjW3MknESnh4ovpjuwXgjvPA/edit?gid=1728349873#gid=1728349873';
            windowFeatures = 'width=1300,height=900,resizable=yes,scrollbars=yes,status=yes';
            break;
        case 'F3':
            url = 'http://dev.itnj.co.kr:28917/';
            windowFeatures = 'width=700,height=950,resizable=yes,scrollbars=yes,status=yes';
            break;
        case 'F4':
            url = 'http://imsform.com';
            break;
        // case 'F6':
        //     try {
        //         const filename = 'd609-8486-141a-27e9-9937';
        //         const response = await fetch(`${baseUrl}/view/${filename}`);
        //         if (response.ok) {
        //             previewFile.value = {
        //                 url: `${baseUrl}/view/${filename}`,
        //                 type: response.headers.get('content-type'),
        //                 downurl: `${baseUrl}/download/${filename}`
        //             };
        //         }
        //     } catch (error) {
        //         console.error('Error previewing file:', error);
        //         toastComponentRef.value?.showToast('error', '알림', '파일 미리보기 중 오류가 발생했습니다.');
        //     }
        //     break;
        default:
            return;
    }
    if (url) {
        event.preventDefault();
        if (event.key === 'F3') {
            window.open(url, '', windowFeatures);
        } else if (event.key === 'F4') {
            window.open(url, '_blank');
        } else {
            window.open(url, '', windowFeatures);
        }
    }
};

onMounted(() => {
    if (isDevelopment) {
        window.addEventListener('keydown', onKeydown);
    }
});

onUnmounted(() => {
    if (isDevelopment) {
        window.removeEventListener('keydown', onKeydown);
    }
});
</script>
<template>
    <div class="layout-wrapper">
        <PreviewPanel v-if="previewFile.url" :fileUrl="previewFile.url" :fileType="previewFile.type" :downurl="previewFile.downurl" :width="'65rem'" :height="'60rem'" @close="closePreview" />
        <app-topbar class="layout-topbar"></app-topbar>

        <div class="layout-main-container">
            <BannerComponent v-if="$route.name === 'reservation'" v-model:isOpen="isPopupOpen" />
            <div class="layout-content">
                <router-view v-slot="{ Component }">
                    <component :is="Component" />
                </router-view>
            </div>
            <app-footer class="layout-footer" v-bind="{ class: 'layout-footer' }"></app-footer>
        </div>

        <div v-if="isDevelopment || (baseUrl && baseUrl.includes('dev.itnj.co.kr'))" class="system-status">
            ➜ DataBase: {{ baseUrl }}
            <br />
            ➜ Server: http://localhost:3000/
            <br />
            ➜ Mode: development
        </div>

        <Toast />
        <DefaultPanel v-if="tipStore.showPopup" :title="tipStore.popupTitle" :width="'30rem'" :height="'45rem'" @close="tipStore.closePopup">
            <template #default>
                <div v-html="tipStore.popupContent"></div>
            </template>
        </DefaultPanel>
    </div>
</template>

<style scoped>
.layout-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}

.layout-topbar {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 4em;
}

.layout-main-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
}

.layout-content {
    flex: 1;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    margin-top: 4rem;
}

/* .layout-footer {
    flex-shrink: 0;
    height: auto;
    background: #f8f9fa;
    text-align: center;
    line-height: 3rem;
    border-top: 1px solid #ddd;
} */

html,
body {
    scrollbar-width: none;
    -ms-overflow-style: none;
}

html::-webkit-scrollbar,
body::-webkit-scrollbar {
    display: none;
}

.system-status {
    position: fixed;
    bottom: 0;
    right: 0.5rem;
    padding: 0.5rem;
    border-radius: 0.25rem;
    font-family: 'SCoreDream400';
    font-size: 0.8rem;
    z-index: 1000;
    color: #181818;
    opacity: 0.5;
}

@media (max-width: 768px) {
    .system-status {
        display: none;
    }
}
</style>
